// fallow-ignore-file code-duplication
// The AI-credit and builder-credit rows are separate display components that intentionally share
// the same "credits row" skeleton (heading + help tooltip + balance).
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { leadingMarkButton } from '@/components/flex-mark';
import plur from 'plur';
import { formatNumber } from '@/utils/number';

type LlmCreditsDisplayProps = {
  availableCredits: number;
  isLoading?: boolean;
  hasError?: boolean;
  /** When provided, renders a "Buy credits" button that opens the AI-credits purchase modal. */
  onPurchase?: () => void;
  isPurchaseDisabled?: boolean;
  purchaseDisabledMessage?: string;
};

/** The read-only AI-credit row: "AI Credits" with a help tooltip and "N credits remaining". */
export function LlmCreditsDisplay({
  availableCredits,
  isLoading,
  hasError,
  onPurchase,
  isPurchaseDisabled,
  purchaseDisabledMessage,
}: LlmCreditsDisplayProps) {
  // Credits are decimal(16,4), so they are shown as whole numbers and the noun agrees with the
  // rounded value.
  const roundedCredits = Math.round(availableCredits);
  let balance = (
    <Text text="product-label">
      {formatNumber(availableCredits, { round: true })} {plur('credit', roundedCredits)} remaining
    </Text>
  );
  if (hasError === true) {
    balance = (
      <Text text="product-label" color="secondary">
        Couldn't load credits
      </Text>
    );
  } else if (isLoading === true) {
    balance = (
      <Text text="product-label" color="secondary">
        Loading…
      </Text>
    );
  }

  return (
    <>
      <Divider my="200" />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Heading level="4" text="product-label" display="flex" alignItems="center" gap="050">
          AI credits
          <Tooltip placement="top-end">
            <Icon name="help" size="sm" color="secondary" />
            <TooltipContent>AI credits for LLM-powered actions across all workspaces.</TooltipContent>
          </Tooltip>
        </Heading>
        <Flex alignItems="center" gap="200">
          {balance}
          {onPurchase != null ? (
            <CreditPurchaseButton
              label="Buy credits"
              testId="ai-credits-purchase-button"
              onPurchase={onPurchase}
              isPurchaseDisabled={isPurchaseDisabled === true}
              purchaseDisabledMessage={purchaseDisabledMessage ?? ''}
            />
          ) : null}
        </Flex>
      </Box>
    </>
  );
}

type CreditPurchaseButtonProps = {
  label: string;
  testId: string;
  onPurchase: () => void;
  isPurchaseDisabled: boolean;
  purchaseDisabledMessage: string;
  /** Defaults to secondary, which is what a credit row shows. */
  variant?: 'primary' | 'secondary';
  isFullWidth?: boolean;
  /** Drawn before the label, such as the Flex mark. */
  elementBefore?: ReactNode;
  className?: string;
};

/**
 * A purchase button, with a custom tooltip explaining why it is disabled. Shared by the credit rows
 * and the wallet card's top-ups, so every purchase CTA states its own blocker the same way.
 */
export function CreditPurchaseButton({
  label,
  testId,
  onPurchase,
  isPurchaseDisabled,
  purchaseDisabledMessage,
  variant = 'secondary',
  isFullWidth = false,
  elementBefore,
  className,
}: CreditPurchaseButtonProps) {
  return (
    <Tooltip content={purchaseDisabledMessage} isDisabled={!isPurchaseDisabled} isCustom={true} placement="top">
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          data-test-credit-purchase-button={testId}
          variant={variant}
          size="sm"
          w={isFullWidth ? 'full' : undefined}
          elementBefore={elementBefore}
          className={elementBefore == null ? className : clsx(leadingMarkButton, className)}
          isDisabled={isPurchaseDisabled}
          onClick={onPurchase}
        >
          {label}
        </Button>
      )}
    </Tooltip>
  );
}
